// Прайсы — раздел портала 55, перенесённый в CRM. ТОЛЬКО ЧТЕНИЕ.
//
// Сценарий взят из жизни портала, а не придуман: самый частый запрос коммерческой службы —
// «сколько стоит вот это» по куску названия или артикулу, с ответом «розница, дилер, из
// какого прайса и насколько он свежий». Рядом — остаток 1С, потому что второй вопрос
// всегда «а есть ли оно у нас».
//
// ЧТО ЭТОТ ЭКРАН ОБЯЗАН ГОВОРИТЬ ВСЛУХ, И ПОЧЕМУ
// По прайсу выставляют деньги, а прайс собран автоматическим парсером из Excel поставщиков.
// В данных есть строки, по которым счёт выставлять нельзя (примечание прайса с ценой,
// количество из Excel, прочитанное как цена, дилерская цена выше розничной), и есть цены
// в юанях, которые ядро НЕ пересчитывает. Поэтому:
//   • отметка версии файла (sha256, когда и кем залито) стоит сверху, а не в подвале;
//   • пометки проблем видны в строке и расшифрованы в карточке;
//   • пустые прайс-листы «Гермес» названы прямо, а не спрятаны в ноль.
// Молчание здесь стоит денег, поэтому всё это не «предупреждения на всякий случай».
//
// ОСТАТОК 1С берётся уже существующим способом (api/stock.ts, тот же useStockSearch,
// на котором стоит раздел «Склад») — по артикулу выбранной позиции. Второго способа
// ходить в 1С мы не заводим: у прокси ядра одна функция и один кеш.
import { useMemo, useState } from 'react'

import {
  useПереносыПрайсов, useПозицииПрайса, useСправочникиПрайсов,
} from '../api/prices'
import type { ПозицияПрайса, СправочникиПрайсов, Перенос } from '../api/prices'
import { MIN_QUERY, useStockSearch } from '../api/stock'
import { FilterBar } from '../components/FilterBar'
import { OdataState } from '../components/OdataState'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtText, pluralN } from '../lib/format'
import {
  РАЗМЕР_СТРАНИЦЫ, ПУТИ, естьВнимание, годнаДляКП, почемуНеГодна, цена,
} from '../lib/prices'
import { fmtQty } from '../lib/stock'

/** Значок свежести прайс-листа: те же слова, что на портале 55. */
function Свежесть({ label, kind }: { label: string; kind: string }) {
  const cls = kind === 'ok' ? 'won' : kind === 'bad' ? 'lose' : 'work'
  return <span className={`badge ${cls}`}>{label}</span>
}

/**
 * Отметка версии: из какого файла залито. Стоит в самом верху раздела, потому что это
 * единственный ответ на вопрос «можно ли по этому прайсу считать сегодня».
 */
function ОтметкаВерсии({ перенос }: { перенос: Перенос | null }) {
  if (!перенос) {
    return (
      <div className="notice err">
        <b>Прайсы в ядро не залиты.</b> Ни одного успешного переноса в журнале нет —
        считать по этому разделу нельзя. Нужен прогон <span className="mono">import_prices.py</span>.
      </div>
    )
  }
  return (
    <div className="notice">
      <b>Прайс залит разовым переносом, живого синка нет.</b>{' '}
      Прогон №{перенос.id} от {fmtDate(перенос.started_at, true)}
      {перенос.operator ? `, запустил ${перенос.operator}` : ''}: позиций{' '}
      {перенос.items_total ?? '—'}, из них новых {перенос.items_new ?? '—'}, с изменённой
      ценой {перенос.items_changed ?? '—'}, исчезло из файла {перенос.items_gone ?? '—'}.
      <div className="hint" style={{ marginTop: 6 }}>
        Источник — репозиторий портала 55:{' '}
        {перенос.files.map((f) => (
          <span key={f.file} className="mono" style={{ marginRight: 12 }}>
            {f.file} · {f.bytes} байт · sha256 {f.sha256.slice(0, 12)}…
          </span>
        ))}
      </div>
      <div className="hint">
        Когда поставщик присылает новый прайс, файл меняют в репозитории портала и перенос
        прогоняют заново — до этого CRM показывает то, что залито. Время файла (mtime)
        доверять нельзя: при копировании оно становится текущим, сверяйте sha256.
      </div>
    </div>
  )
}

/** Пометки строки. Показываем коротко в таблице; расшифровка — в карточке справа. */
function Пометки({ коды, подписи }: { коды: string[]; подписи: Record<string, string> }) {
  if (!коды.length) return <span className="muted">—</span>
  return (
    <>
      {коды.map((к) => (
        <span
          key={к}
          className={`badge ${естьВнимание([к]) ? 'lose' : ''}`}
          title={подписи[к] ?? к}
          style={{ marginRight: 4 }}
        >
          {к.replace(/_/g, ' ')}
        </span>
      ))}
    </>
  )
}

/** Остаток 1С по артикулу выбранной позиции. Тот же хук, что у раздела «Склад». */
function ОстатокПоАртикулу({ art }: { art: string }) {
  const поиск = useStockSearch(art)
  if (art.length < MIN_QUERY) {
    return (
      <div className="hint">
        У позиции пустой артикул — остаток в 1С по ней не найти. Так у части позиций
        прайса: в файле артикул не заполнен.
      </div>
    )
  }
  // Артикул в 1С может отличаться от артикула прайса — связи между ними в данных нет,
  // поэтому показываем ВСЕ совпадения поиска, а не «остаток позиции», и говорим об этом.
  return (
    <OdataState isLoading={поиск.isLoading} error={поиск.error}>
      {(поиск.data?.items.length ?? 0) === 0 ? (
        <div className="hint">
          В 1С по артикулу <span className="mono">{art}</span> номенклатура не нашлась.
          Связи прайса с номенклатурой 1С в данных нет — ищем по совпадению артикула.
        </div>
      ) : (
        <>
          {/* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */}
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Номенклатура 1С</th>
                  <th className="num">Всего</th>
                  <th className="num">Резерв</th>
                  <th className="num">Свободно</th>
                </tr>
              </thead>
              <tbody>
                {поиск.data!.items.slice(0, 6).map((i) => (
                  <tr key={i.ref}>
                    <td data-label="Номенклатура 1С">{fmtText(i.name)}</td>
                    <td className="num" data-label="Всего">{i.have === null ? '—' : fmtQty(i.have)}</td>
                    <td className="num" data-label="Резерв">{i.reserve === null ? '—' : fmtQty(i.reserve)}</td>
                    <td className="num" data-label="Свободно">{i.free === null ? '—' : fmtQty(i.free)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="hint" style={{ marginTop: 6 }}>
            Поиск по артикулу <span className="mono">{art}</span> в 1С. Связи «позиция прайса ↔
            номенклатура 1С» в данных нет, поэтому это совпадение по артикулу, а не
            гарантированно та же позиция.
          </div>
        </>
      )}
    </OdataState>
  )
}

/** Карточка выбранной позиции: все три цены, пометки с расшифровкой, остаток 1С. */
function Карточка({ п, refs }: { п: ПозицияПрайса; refs: СправочникиПрайсов }) {
  const годна = годнаДляКП(п)
  const почему = почемуНеГодна(п)
  return (
    <>
      <div className="card">
        <h2>{п.name}</h2>
        <dl className="dl">
          <dt>Артикул</dt>
          <dd className="mono">{п.pn || <span className="muted">не заполнен в прайсе</span>}</dd>
          <dt>Модель</dt>
          <dd className="mono">{fmtText(п.model)}</dd>
          <dt>Производитель</dt>
          <dd>{fmtText(п.vendor)}</dd>
          <dt>Раздел прайса</dt>
          <dd>{fmtText(п.section)}</dd>
          <dt>Прайс-лист</dt>
          <dd>
            {п.list_name}
            {п.list_date ? <span className="muted"> · от {fmtDate(п.list_date)}</span> : null}
          </dd>
          <dt>Розница (РРЦ)</dt>
          <dd className="num">{цена(п.price_user, п.cur_user)}</dd>
          <dt>Дилерская</dt>
          <dd className="num">{цена(п.price_partner, п.cur_partner)}</dd>
          <dt>Закупка</dt>
          <dd className="num">
            {цена(п.price_input, п.cur_input)}
            {п.price_input === null
              ? <span className="hint"> закупочная цена есть только в прайсе СиР</span>
              : null}
          </dd>
          <dt>В файле портала</dt>
          <dd className="mono">{п.source_file}, строка {п.row_no}</dd>
        </dl>

        {п.price_changed_at && (
          <div className="notice" style={{ marginTop: 12, marginBottom: 0 }}>
            Цена менялась на последнем переносе: было {цена(п.prev_price_user, п.cur_user)},
            стало {цена(п.price_user, п.cur_user)} ({fmtDate(п.price_changed_at, true)}).
          </div>
        )}

        {!годна && (
          <div className="notice err" style={{ marginTop: 12, marginBottom: 0 }}>
            <b>В КП по этой цене считать нельзя:</b> {почему}. Позицию можно добавить
            в предложение, но цену придётся ввести рублями вручную.
          </div>
        )}
      </div>

      {п.problems.length > 0 && (
        <div className="card">
          <h2>Что не так с этой строкой прайса</h2>
          <ul className="plain-list">
            {п.problems.map((к) => (
              <li key={к}>
                <b>{к.replace(/_/g, ' ')}</b>
                <div className="hint">{refs.problem_labels[к] ?? 'пояснения к этому коду нет'}</div>
              </li>
            ))}
          </ul>
          <div className="hint" style={{ marginTop: 10 }}>
            Мы ничего не исправляли: источника правды по цене у CRM нет, а «поправить»
            прайс поставщика она не может. Строка показана как есть, с пометками.
          </div>
        </div>
      )}

      <div className="card">
        <h2>Остаток в 1С</h2>
        <ОстатокПоАртикулу art={(п.pn || '').trim()} />
      </div>
    </>
  )
}

export default function Prices() {
  const [ввод, setВвод] = useState('')
  const [лист, setЛист] = useState('')
  const [поставщик, setПоставщик] = useState('')
  const [раздел, setРаздел] = useState('')
  const [проблема, setПроблема] = useState('')
  const [толькоГодные, setТолькоГодные] = useState(false)
  const [старт, setСтарт] = useState(0)
  const [выбран, setВыбран] = useState<string | null>(null)

  const refs = useСправочникиПрайсов()
  const переносы = useПереносыПрайсов()

  const q = ввод.trim()
  const короткий = q.length > 0 && q.length < MIN_QUERY

  const фильтры = useMemo(() => ({
    q: короткий ? undefined : (q || undefined),
    list: лист || undefined,
    vendor: поставщик || undefined,
    section: раздел || undefined,
    problem: проблема || undefined,
    quotable: толькоГодные ? '1' : undefined,
    start: старт,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [q, короткий, лист, поставщик, раздел, проблема, толькоГодные, старт])

  const позиции = useПозицииПрайса(фильтры, !короткий)
  const данные = позиции.data
  const строки = данные?.rows ?? []
  const текущая = строки.find((r) => r.src_key === выбран) ?? null

  const сСброса = <T,>(setter: (v: T) => void) => (v: T) => { setСтарт(0); setter(v) }
  const подписи = refs.data?.problem_labels ?? {}

  return (
    <>
      <div className="page-head">
        <h1>Прайсы</h1>
        <div className="spacer" />
        <span className="muted">
          {данные ? `найдено: ${данные.total}` : ''}
          {refs.data ? ` · в ядре ${pluralN(refs.data.counts.items,
            'позиция', 'позиции', 'позиций')},`
            + ` считать КП можно по ${refs.data.counts.quotable}` : ''}
        </span>
      </div>

      <QueryState isLoading={refs.isLoading} error={refs.error}>
        <ОтметкаВерсии перенос={переносы.data?.rows[0] ?? refs.data?.last_import ?? null} />

        {refs.data && (
          <div className="card" style={{ marginBottom: 16 }}>
            <h2>Прайс-листы</h2>
            <div className="table-wrap cards">
              <table>
                <thead>
                  <tr>
                    <th>Прайс-лист</th>
                    <th>Поставщик</th>
                    <th className="nowrap">Дата</th>
                    <th className="nowrap">Свежесть</th>
                    <th className="num">Позиций</th>
                    <th className="num">С рублёвой ценой</th>
                    <th>Файл</th>
                  </tr>
                </thead>
                <tbody>
                  {refs.data.lists.map((l) => (
                    <tr
                      key={l.src_name}
                      onClick={() => сСброса(setЛист)(лист === l.src_name ? '' : l.src_name)}
                      style={{
                        cursor: 'pointer',
                        background: лист === l.src_name ? 'var(--accent-soft)' : undefined,
                      }}
                    >
                      <td data-label="Прайс-лист">{l.src_name}</td>
                      <td data-label="Поставщик">{fmtText(l.vendor)}</td>
                      <td className="nowrap" data-label="Дата">{fmtDate(l.list_date)}</td>
                      <td className="nowrap" data-label="Свежесть"><Свежесть {...l.fresh} /></td>
                      <td className="num" data-label="Позиций">{l.items_count}</td>
                      <td className="num" data-label="С рублёвой ценой">{l.items_rub}</td>
                      <td className="mono" data-label="Файл">{l.source_file}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Про пустые листы говорим только когда они есть: иначе после следующего
                переноса пояснение осталось бы висеть неправдой. */}
            {refs.data.lists.some((l) => l.items_count === 0) && (
              <div className="hint" style={{ marginTop: 8 }}>{refs.data.data_notes.empty_lists}</div>
            )}
            <div className="hint" style={{ marginTop: 8 }}>
              Строка прайс-листа — это фильтр: нажмите, чтобы показать только его позиции.
            </div>
            {refs.data.counts.in_currency > 0 && (
              <div className="hint">{refs.data.data_notes.no_fx}</div>
            )}
          </div>
        )}

        <div className="card" style={{ marginBottom: 16 }}>
          <FilterBar id="prices">
            <div className="f" style={{ flex: 2, minWidth: 240 }}>
              <label htmlFor="pr-q">Название или артикул</label>
              <input
                id="pr-q" value={ввод} autoFocus
                onChange={(e) => сСброса(setВвод)(e.target.value)}
                placeholder="например: гирокомпас или NGC-5010"
              />
            </div>
            <div className="f" style={{ minWidth: 190 }}>
              <label htmlFor="pr-list">Прайс-лист</label>
              <select id="pr-list" value={лист} onChange={(e) => сСброса(setЛист)(e.target.value)}>
                <option value="">любой</option>
                {(refs.data?.lists ?? []).filter((l) => l.items_count > 0).map((l) => (
                  <option key={l.src_name} value={l.src_name}>
                    {l.src_name} ({l.items_count})
                  </option>
                ))}
              </select>
            </div>
            <div className="f" style={{ minWidth: 160 }}>
              <label htmlFor="pr-vendor">Производитель</label>
              <select id="pr-vendor" value={поставщик}
                      onChange={(e) => сСброса(setПоставщик)(e.target.value)}>
                <option value="">любой</option>
                {Object.entries(refs.data?.vendors ?? {}).map(([к, n]) => (
                  <option key={к} value={к}>{к || '(без производителя)'} ({n})</option>
                ))}
              </select>
            </div>
            <div className="f" style={{ minWidth: 170 }}>
              <label htmlFor="pr-section">Раздел прайса</label>
              <select id="pr-section" value={раздел}
                      onChange={(e) => сСброса(setРаздел)(e.target.value)}>
                <option value="">любой</option>
                {Object.entries(refs.data?.sections ?? {}).map(([к, n]) => (
                  <option key={к} value={к}>{к || '(без раздела)'} ({n})</option>
                ))}
              </select>
            </div>
            <div className="f" style={{ minWidth: 200 }}>
              <label htmlFor="pr-problem">Пометка данных</label>
              <select id="pr-problem" value={проблема}
                      onChange={(e) => сСброса(setПроблема)(e.target.value)}>
                <option value="">любая</option>
                {Object.entries(refs.data?.problems ?? {}).map(([к, n]) => (
                  <option key={к} value={к}>{к.replace(/_/g, ' ')} ({n})</option>
                ))}
              </select>
            </div>
            <div className="f">
              <label htmlFor="pr-quot">Только те, что можно посчитать</label>
              <input id="pr-quot" type="checkbox" checked={толькоГодные}
                     onChange={(e) => сСброса(setТолькоГодные)(e.target.checked)} />
            </div>
          </FilterBar>
          <div className="hint" style={{ marginTop: 10 }}>
            Ищем по названию, артикулу и модели одной строкой — как на портале 55. Точное
            совпадение артикула ядро ставит первым. Минимум {MIN_QUERY} символа.
            {' '}Собрать предложение из найденного — в разделе «Конструктор КП»
            (<span className="mono">{ПУТИ.конструкторКП}</span>).
          </div>
        </div>

        {короткий ? (
          <div className="card muted">Введите минимум {MIN_QUERY} символа.</div>
        ) : (
          <QueryState isLoading={позиции.isLoading} error={позиции.error}>
            <div className="grid-2">
              <div className="card">
                <div className="page-head" style={{ marginBottom: 10 }}>
                  <h2 style={{ margin: 0 }}>Позиции</h2>
                  <div className="spacer" />
                  <span className="hint">
                    {позиции.isFetching ? 'обновляю…' : ''}
                  </span>
                </div>

                <div className="table-wrap cards">
                  <table>
                    <thead>
                      <tr>
                        <th className="nowrap">Артикул</th>
                        <th>Наименование</th>
                        <th className="num">Розница</th>
                        <th className="num">Дилер</th>
                        <th className="nowrap">Прайс-лист</th>
                        <th>Пометки</th>
                      </tr>
                    </thead>
                    <tbody>
                      {строки.map((r) => (
                        <tr
                          key={r.src_key}
                          onClick={() => setВыбран(r.src_key)}
                          style={{
                            cursor: 'pointer',
                            background: r.src_key === выбран ? 'var(--accent-soft)' : undefined,
                          }}
                        >
                          <td className="mono nowrap" data-label="Артикул">
                            {r.pn || <span className="muted">—</span>}
                            {r.exact && <span className="badge won" style={{ marginLeft: 6 }}>точно</span>}
                          </td>
                          <td data-label="Наименование">{r.name}</td>
                          <td className="num" data-label="Розница">
                            {цена(r.price_user, r.cur_user)}
                            {!r.quotable && (
                              <div className="hint">{почемуНеГодна(r)}</div>
                            )}
                          </td>
                          <td className="num" data-label="Дилер">{цена(r.price_partner, r.cur_partner)}</td>
                          <td className="nowrap" style={{ fontSize: 12 }} data-label="Прайс-лист">
                            {r.list_name}
                            <div className="hint">{fmtDate(r.list_date)}</div>
                          </td>
                          <td data-label="Пометки"><Пометки коды={r.problems} подписи={подписи} /></td>
                        </tr>
                      ))}
                      {строки.length === 0 && (
                        <tr>
                          <td colSpan={6} className="empty">
                            <div className="empty-title">Ничего не нашлось</div>
                            <div className="empty-hint">
                              В прайсах{' '}
                              {pluralN(refs.data?.counts.items ?? 0,
                                'позиция', 'позиции', 'позиций')} из трёх
                              прайс-листов. Если позиция должна быть — возможно, прайс
                              поставщика ещё не залит: перенос делается вручную.
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {данные && данные.total > РАЗМЕР_СТРАНИЦЫ && (
                  <div className="pager">
                    <button disabled={старт === 0 || позиции.isFetching}
                            onClick={() => setСтарт(Math.max(0, старт - РАЗМЕР_СТРАНИЦЫ))}>
                      ← назад
                    </button>
                    <span className="muted">
                      {старт + 1}–{Math.min(старт + РАЗМЕР_СТРАНИЦЫ, данные.total)} из {данные.total}
                    </span>
                    <button disabled={данные.next === null || позиции.isFetching}
                            onClick={() => setСтарт(данные.next!)}>
                      вперёд →
                    </button>
                  </div>
                )}
              </div>

              <div>
                {текущая && refs.data ? (
                  <Карточка п={текущая} refs={refs.data} />
                ) : (
                  <div className="card muted">
                    Выберите позицию слева — здесь покажем все три цены, пометки прайса
                    с расшифровкой и остаток в 1С по артикулу.
                  </div>
                )}

                <div className="card">
                  <h2>Почему здесь нельзя ничего изменить</h2>
                  <p className="muted" style={{ marginTop: 0 }}>
                    {refs.data?.data_notes.source}
                  </p>
                  <p className="muted">{refs.data?.data_notes.read_only}</p>
                </div>
              </div>
            </div>
          </QueryState>
        )}
      </QueryState>
    </>
  )
}
